import moment from 'moment';
import {
  BLOCK_KEY_SEPARATOR,
  DAY_PLAN_IMPORT_FAILED_KEY,
  LOW_CONFIDENCE_THRESHOLD
} from 'constants/day-plan';
import { ASYNC_RESULT_STATUS, DATE_FORMAT_YMD } from 'constants/general';
import {
  FileImportExtractPatch,
  FileImportStatusMode,
  DayPlanEventIdNamespace,
  ImportEventRepeat,
  PlannedBlockKeyPrefix,
  UnscheduledPromoteKind
} from 'enums';
import {
  CreateDayPlanEventItem,
  ImportPhotoResult,
  FileImportEvent,
  FileImportEventDateGroup,
  FileImportEventSeries,
  FileImportOptions,
  FileImportUiState,
  FileImportUnscheduledEntry
} from 'interfaces/day-plan.interface';
import { DayPlanFileUploadError } from 'services/day-plan-file-upload';
import { captureException } from 'services/posthog.service';
import {
  combineLocalDateTimeWithSeconds,
  formatDateToYMD,
  parseYmdMoment
} from 'utils/time.util';

export const toImportedEventBlockKey = (importId: string): string =>
  `${PlannedBlockKeyPrefix.EVENT}${BLOCK_KEY_SEPARATOR}${DayPlanEventIdNamespace.IMPORT}${BLOCK_KEY_SEPARATOR}${importId}`;

export const isLowConfidence = (confidence: number): boolean =>
  confidence < LOW_CONFIDENCE_THRESHOLD;

/**
 * Planned date for a file-import row: use API date when valid YYYY-MM-DD;
 * null/empty/invalid fall back to today's day key.
 */
export const normalizeFileImportEventDate = (
  date: string | null | undefined
): string => {
  const today = formatDateToYMD(new Date());
  if (date == null || date.length === 0) {
    return today;
  }
  if (parseYmdMoment(date) == null) {
    return today;
  }
  return date;
};

const compareFileImportEvents = (
  left: FileImportEvent,
  right: FileImportEvent
): number => {
  const leftDate = left.date ?? '';
  const rightDate = right.date ?? '';
  const dateCompare = leftDate.localeCompare(rightDate);
  if (dateCompare !== 0) {
    return dateCompare;
  }
  const leftTime = left.start_time;
  const rightTime = right.start_time;
  if (leftTime === null && rightTime === null) {
    return left.title.localeCompare(right.title);
  }
  if (leftTime === null) {
    return 1;
  }
  if (rightTime === null) {
    return -1;
  }
  const timeCompare = leftTime.localeCompare(rightTime);
  if (timeCompare !== 0) {
    return timeCompare;
  }
  return left.title.localeCompare(right.title);
};

/**
 * Normalize each extract row's date, then sort chronologically for display.
 */
export const normalizeFileImportEvents = (
  events: FileImportEvent[]
): FileImportEvent[] =>
  events
    .map((event) => ({
      ...event,
      date: normalizeFileImportEventDate(event.date)
    }))
    .sort(compareFileImportEvents);

/** Normalize recurring drafts for review (dates only; times stay as returned). */
export const normalizeFileImportEventSeries = (
  series: FileImportEventSeries[]
): FileImportEventSeries[] =>
  series
    .map((row) => ({
      ...row,
      date: normalizeFileImportEventDate(row.date),
      repeat_end_on: normalizeFileImportEventDate(row.repeat_end_on)
    }))
    .sort((left, right) => {
      const byDate = left.date.localeCompare(right.date);
      if (byDate !== 0) {
        return byDate;
      }
      return left.start_time.localeCompare(right.start_time);
    });

/** Normalize incomplete-time drafts for review. */
export const normalizeFileImportUnscheduledEntries = (
  entries: FileImportUnscheduledEntry[]
): FileImportUnscheduledEntry[] =>
  entries.map((entry) => ({
    ...entry,
    date: normalizeFileImportEventDate(entry.date),
    repeat_end_on:
      entry.repeat_end_on == null || entry.repeat_end_on.length === 0
        ? null
        : normalizeFileImportEventDate(entry.repeat_end_on)
  }));

export const hasValidFileImportTimeRange = ({
  date,
  startTime,
  endTime
}: {
  date: string;
  startTime: string | null;
  endTime: string | null;
}): boolean => {
  if (startTime == null || endTime == null) {
    return false;
  }
  const plannedDate = normalizeFileImportEventDate(date);
  const start = moment(
    `${plannedDate} ${startTime}`,
    `${DATE_FORMAT_YMD} HH:mm`,
    true
  );
  const end = moment(
    `${plannedDate} ${endTime}`,
    `${DATE_FORMAT_YMD} HH:mm`,
    true
  );
  return start.isValid() && end.isValid() && end.isAfter(start);
};

export type UnscheduledPromoteResult =
  | { kind: UnscheduledPromoteKind.STAY; entry: FileImportUnscheduledEntry }
  | { kind: UnscheduledPromoteKind.EVENT; event: FileImportEvent }
  | { kind: UnscheduledPromoteKind.SERIES; series: FileImportEventSeries };

const toOneOffFromUnscheduled = (
  entry: FileImportUnscheduledEntry,
  date: string
): FileImportEvent => ({
  title: entry.title,
  date,
  start_time: entry.start_time,
  end_time: entry.end_time,
  confidence: entry.confidence
});

const toSeriesFromUnscheduled = (
  entry: FileImportUnscheduledEntry,
  date: string
): FileImportEventSeries | null => {
  if (entry.repeat === ImportEventRepeat.NONE) {
    return null;
  }
  if (entry.repeat_end_on == null || entry.repeat_end_on.length === 0) {
    return null;
  }
  if (entry.start_time == null || entry.end_time == null) {
    return null;
  }
  return {
    title: entry.title,
    date,
    start_time: entry.start_time,
    end_time: entry.end_time,
    confidence: entry.confidence,
    repeat: entry.repeat,
    repeat_end_on: normalizeFileImportEventDate(entry.repeat_end_on)
  };
};

/** Promote an unscheduled row once start+end (and series end date) are valid. */
export const resolveUnscheduledPromotion = (
  entry: FileImportUnscheduledEntry
): UnscheduledPromoteResult => {
  const date = normalizeFileImportEventDate(entry.date);
  const normalized: FileImportUnscheduledEntry = { ...entry, date };
  const hasTimes = hasValidFileImportTimeRange({
    date,
    startTime: entry.start_time,
    endTime: entry.end_time
  });
  if (!hasTimes) {
    return { kind: UnscheduledPromoteKind.STAY, entry: normalized };
  }
  if (entry.repeat === ImportEventRepeat.NONE) {
    return {
      kind: UnscheduledPromoteKind.EVENT,
      event: toOneOffFromUnscheduled(entry, date)
    };
  }
  const series = toSeriesFromUnscheduled(entry, date);
  if (series == null) {
    return { kind: UnscheduledPromoteKind.STAY, entry: normalized };
  }
  return { kind: UnscheduledPromoteKind.SERIES, series };
};

/** Build `POST /day-plan/events` items from reviewed one-off rows. */
export const buildCreateDayPlanEventItems = ({
  events,
  timezone
}: {
  events: FileImportEvent[];
  timezone: string;
}): CreateDayPlanEventItem[] => {
  const items: CreateDayPlanEventItem[] = [];
  events.forEach((event) => {
    if (event.start_time == null || event.end_time == null) {
      return;
    }
    const date = normalizeFileImportEventDate(event.date);
    const dtstart = combineLocalDateTimeWithSeconds(date, event.start_time);
    const dtend = combineLocalDateTimeWithSeconds(date, event.end_time);
    if (dtstart == null || dtend == null) {
      return;
    }
    items.push({
      title: event.title.trim(),
      dtstart_local: dtstart,
      dtend_local: dtend,
      timezone
    });
  });
  return items;
};

/**
 * Group extract rows by planned date for the review accordion.
 * Preserves each row's index in the flat Redux list for update/remove.
 */
export const groupFileImportEventsByDate = (
  events: FileImportEvent[]
): FileImportEventDateGroup[] => {
  const groups = new Map<string, FileImportEventDateGroup>();
  events.forEach((event, index) => {
    const date = normalizeFileImportEventDate(event.date);
    const existing = groups.get(date);
    if (existing !== undefined) {
      existing.events.push(event);
      existing.indices.push(index);
      return;
    }
    groups.set(date, { date, events: [event], indices: [index] });
  });
  return Array.from(groups.values()).sort((left, right) =>
    left.date.localeCompare(right.date)
  );
};

export const getFileImportReviewSummary = (
  groups: FileImportEventDateGroup[]
): {
  eventCount: number;
  dayCount: number;
  fromDate: string;
  toDate: string;
} | null => {
  if (groups.length < 2) {
    return null;
  }
  const eventCount = groups.reduce(
    (total, group) => total + group.events.length,
    0
  );
  return {
    eventCount,
    dayCount: groups.length,
    fromDate: groups[0].date,
    toDate: groups[groups.length - 1].date
  };
};

/** Default end date for recurring-event detection: Dec 31 of the current year. */
export const getDefaultRecurringEventsEndOn = (): string =>
  moment().endOf('year').format(DATE_FORMAT_YMD);

export const isDayPlanImportImageMime = (mimeType: string): boolean =>
  mimeType.startsWith('image/');

export const createInitialImportOptions = (): FileImportOptions => ({
  detectRecurring: false,
  repeatedEventsEndOn: getDefaultRecurringEventsEndOn()
});

export const createInitialFileImportUi = (): FileImportUiState => ({
  isUploading: false,
  pendingFile: null,
  previewUrl: null,
  isOptionsOpen: false,
  options: createInitialImportOptions(),
  statusMode: null,
  errorMessageKey: DAY_PLAN_IMPORT_FAILED_KEY
});

export const getUploadErrorKey = (error: unknown): string => {
  if (error instanceof DayPlanFileUploadError) {
    return error.message;
  }
  captureException(error);
  return 'day_plan.file_upload_failed';
};

export const getExtractStatusPatch = (
  result: ImportPhotoResult
): Partial<FileImportUiState> | FileImportExtractPatch => {
  if (result.type === ASYNC_RESULT_STATUS.REJECTED) {
    return {
      errorMessageKey: DAY_PLAN_IMPORT_FAILED_KEY,
      statusMode: FileImportStatusMode.FAILED
    };
  }
  if (
    result.events.length === 0 &&
    result.event_series.length === 0 &&
    result.unscheduled_entries.length === 0
  ) {
    return { statusMode: FileImportStatusMode.EMPTY };
  }
  return FileImportExtractPatch.CLEAR;
};

export const replacePreviewObjectUrl = ({
  previousUrl,
  file
}: {
  previousUrl: string | null;
  file: File | null;
}): string | null => {
  if (previousUrl !== null) {
    URL.revokeObjectURL(previousUrl);
  }
  if (file === null || !isDayPlanImportImageMime(file.type)) {
    return null;
  }
  return URL.createObjectURL(file);
};

/** Prefer today when present in the group list; otherwise the first date. */
export const getDefaultFileImportOpenDate = (
  dates: string[]
): string | null => {
  if (dates.length === 0) {
    return null;
  }
  const today = formatDateToYMD(new Date());
  return dates.includes(today) ? today : dates[0];
};
